/**
 * lib/sanitize.ts
 * Input sanitisation to prevent XSS in stored/displayed user content.
 * Mirrors the v1 sanitize API in TypeScript.
 */
import DOMPurify from 'dompurify';

const HTML_ENTITIES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;',
  '`': '&#x60;',
  '=': '&#x3D;',
};

export function escapeHtml(input: unknown): string {
  if (typeof input !== 'string') return '';
  return input.replace(/[&<>"'`=/]/g, (c) => HTML_ENTITIES[c]);
}

export function stripHtml(input: unknown): string {
  if (typeof input !== 'string') return '';
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Plain text fields (names, admission numbers). */
export function sanitizeText(input: unknown, maxLength = 200): string {
  if (typeof input !== 'string') return '';
  return stripHtml(input).slice(0, maxLength);
}

/** Free-text comments — strips HTML but preserves line breaks. */
export function sanitizeComment(input: unknown, maxLength = 2000): string {
  if (typeof input !== 'string') return '';
  const noHtml = input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<[^>]*>/g, '')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return noHtml.slice(0, maxLength);
}

/** Email — basic shape validation + lowercasing. */
export function sanitizeEmail(input: unknown): string {
  if (typeof input !== 'string') return '';
  const v = input.trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? v : '';
}

/** WhatsApp / phone — keep digits and a leading +. */
export function sanitizePhone(input: unknown): string {
  if (typeof input !== 'string') return '';
  const cleaned = input.replace(/[^\d+]/g, '');
  return cleaned.replace(/(?!^)\+/g, '').slice(0, 20);
}

/** Rich HTML (rare — only where formatting is intentionally allowed). */
export function sanitizeHtml(input: unknown): string {
  if (typeof input !== 'string') return '';
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br', 'p', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: [],
  });
}

export default {
  escapeHtml,
  stripHtml,
  sanitizeText,
  sanitizeComment,
  sanitizeEmail,
  sanitizePhone,
  sanitizeHtml,
};

// Foundation round: sentinel-aware stripUndefined
/**
 * Recursively strip keys whose value is `undefined` from an object.
 *
 * Firestore rejects `undefined` field values anywhere in a write
 * payload. This lets us build payload objects with optional fields
 * that may resolve to undefined and clean them in one pass.
 *
 * Sentinel-aware: detects Firestore field-value sentinels (the
 * objects returned by serverTimestamp(), arrayUnion(), arrayRemove(),
 * deleteField(), and increment()) by their internal `_methodName`
 * discriminator and leaves them untouched. This is essential — if
 * we recurse into a sentinel we strip its discriminator and Firestore
 * stops recognising it as a sentinel.
 *
 * The function is a no-op on payloads that don't contain undefineds,
 * so it's safe to apply universally. Performance impact is one shallow
 * traversal per write — negligible.
 */
export function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  const clean = {} as Record<string, unknown>;
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined) continue;
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
      // CRITICAL: don't recurse into Firestore sentinels. They look
      // like { _methodName: 'arrayUnion', _elements: [...] } etc.;
      // recursion would strip those internals and break the sentinel.
      if ('_methodName' in (v as Record<string, unknown>)) {
        clean[k] = v;
      } else {
        clean[k] = stripUndefined(v as Record<string, unknown>);
      }
    } else if (Array.isArray(v)) {
      clean[k] = v.map((item) =>
        item !== null && typeof item === 'object' && !Array.isArray(item)
          ? '_methodName' in (item as Record<string, unknown>)
            ? item
            : stripUndefined(item as Record<string, unknown>)
          : item,
      );
    } else {
      clean[k] = v;
    }
  }
  return clean as T;
}
